import { encryptToUrl, decryptFromUrl } from '../utils/crypto';

const STORAGE_KEY = 'kaarya_user';
const SESSION_EXPIRED_KEY = 'kaarya_session_expired';
const REMEMBER_KEY = 'kaarya_remember';

const decryptStored = () => decryptFromUrl(window.localStorage.getItem(STORAGE_KEY));

const AuthService = {
  getApiKey() {
    const data = decryptStored();
    return data?.apiKey || null;
  },

  // Returns the full decrypted payload stored in localStorage.
  getUser() {
    return decryptStored();
  },

  // Lightweight helper for screens that only need high-level user info
  // (shape tailored for Tickets.jsx and similar views).
  getUserInfo() {
    const data = decryptStored();
    if (!data) return null;

    const selected = data.selectedRegistration || null;
    const student = data.student || null;

    const registrationNumber =
      selected?.registrationNumber || student?.registrationNumber || null;

    return {
      // Student portal uses only student logins today
      type: data.type || 'student',
      registrationNumber,
      studentId: data.studentId || student?.studentId || null,
      studentName: student?.fullName || null,
      raw: data,
    };
  },

  // Helper to expose currently selected course/registration context.
  // This is essentially the "selectedRegistration" object from the
  // encrypted payload, normalized to a smaller shape.
  getCourseInfo() {
    const data = decryptStored();
    if (!data) return null;

    const selected = data.selectedRegistration;
    if (!selected) return null;

    return {
      registrationId: selected.registrationId,
      registrationNumber: selected.registrationNumber,
      courseId: selected.courseId,
      courseName: selected.courseName || selected.courseShortName || null,
      courseShortName: selected.courseShortName || null,
      batchId: selected.batchId,
      batchName: selected.batchName,
      raw: selected,
    };
  },

  clear() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (_) {}
  },

  // Set right before a forced redirect to /login so that page can greet the
  // student with a proper explanation instead of dropping them back at a
  // blank sign-in form. sessionStorage (not localStorage) so it can only
  // ever apply to the redirect that set it, never linger into a later visit.
  flagSessionExpired() {
    try {
      window.sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
    } catch (_) {}
  },

  // Reads and immediately clears the flag, so it only ever fires once.
  consumeSessionExpiredFlag() {
    try {
      const flagged = window.sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1';
      window.sessionStorage.removeItem(SESSION_EXPIRED_KEY);
      return flagged;
    } catch (_) {
      return false;
    }
  },

  // "Remember me" on the login form -- independent of the signed-in session
  // above, so it survives Logout (the whole point is to still have the
  // fields pre-filled the next time someone signs back in on this device).
  rememberCredentials(username, password) {
    try {
      window.localStorage.setItem(REMEMBER_KEY, encryptToUrl({ username, password }));
    } catch (_) {}
  },

  getRememberedCredentials() {
    try {
      return decryptFromUrl(window.localStorage.getItem(REMEMBER_KEY));
    } catch (_) {
      return null;
    }
  },

  forgetCredentials() {
    try {
      window.localStorage.removeItem(REMEMBER_KEY);
    } catch (_) {}
  },
};

export default AuthService;
